import React, { useState, useEffect, useMemo } from 'react';

const AUTO_ADVANCE_MS = 4000;

const heroBannerContext = require.context(
  '../assets/hero banner/3',
  false,
  /\.(jpe?g|png|webp)$/i
);

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobileView, setIsMobileView] = useState(false);

  const heroImageEntries = useMemo(() => {
    const keys = heroBannerContext.keys().sort();
    return keys.map((key) => ({ key, src: heroBannerContext(key) }));
  }, []);

  const mobileHeroImages = useMemo(
    () =>
      heroImageEntries
        .filter((entry) => /hero_banner_mobile[12]\.jpe?g$/i.test(entry.key))
        .map((entry) => entry.src),
    [heroImageEntries]
  );

  const desktopHeroImages = useMemo(
    () =>
      heroImageEntries
        .filter((entry) => !/hero_banner_mobile[12]\.jpe?g$/i.test(entry.key))
        .map((entry) => entry.src),
    [heroImageEntries]
  );

  const activeImages = useMemo(() => {
    if (isMobileView) {
      return mobileHeroImages.length > 0 ? mobileHeroImages : desktopHeroImages;
    }
    return desktopHeroImages.length > 0 ? desktopHeroImages : heroImageEntries.map((entry) => entry.src);
  }, [desktopHeroImages, heroImageEntries, isMobileView, mobileHeroImages]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateView = () => setIsMobileView(mediaQuery.matches);
    updateView();
    mediaQuery.addEventListener('change', updateView);
    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [activeImages.length, isMobileView]);

  useEffect(() => {
    if (activeImages.length <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev === activeImages.length - 1 ? 0 : prev + 1));
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(interval);
  }, [activeImages.length]);

  return (
    <>
      <section className="relative w-full aspect-[4/5] sm:aspect-[16/9] max-h-[80vh] sm:max-h-[75vh] mx-auto flex items-center px-4 sm:px-8 md:px-16 overflow-hidden bg-white">
        {/* Background Image Carousel — mobile 4:5 with dedicated mobile banners */}
        <div className="absolute inset-0 z-0">
          <div className="relative h-full w-full bg-white">
            {activeImages.length === 0 ? (
              <div className="absolute inset-0 bg-stone-100" aria-hidden />
            ) : (
              activeImages.map((image, index) => (
                <img
                  key={index}
                  className={`absolute inset-0 h-full w-full object-cover sm:object-contain object-center transition-opacity duration-1000 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                  alt={`Jatin Interiors hero banner ${index + 1}`}
                  src={image}
                  decoding="async"
                />
              ))
            )}
            <div className="absolute inset-0 bg-black/5"></div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10">
          {/* Content removed as requested */}
        </div>

        {activeImages.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
            {activeImages.map((_, index) => (
              <button
                type="button"
                key={index}
                aria-label={`Show slide ${index + 1}`}
                className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                  index === currentImageIndex
                    ? 'bg-white w-8 sm:w-12'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default Hero;
